import { createFileRoute } from '@tanstack/react-router'
import { Plus, Receipt, Filter, Check, X as XIcon, DollarSign } from 'lucide-react'
import { useState } from 'react'

import { BatchApprovalToolbar } from '@/components/common/batch-approval-toolbar'
import {
  EmptyState,
  NoResultsEmptyState,
  determineEmptyStateType,
} from '@/components/common/empty-state'
import { ExportButton } from '@/components/common/export-button'
import { UrgeButton } from '@/components/common/urge-button'
import { ReimbursementForm } from '@/components/finance/reimbursement/reimbursement-form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/toast'
import { useAccounts, useConfirmDialog, useCurrencyFormatter } from '@/hooks'
import {
  useReimbursements,
  useCancelReimbursement,
  useApproveReimbursement,
  useRejectReimbursement,
  usePayReimbursement,
  useBatchApproveReimbursements,
  useBatchRejectReimbursements,
  type ReimbursementListParams,
  type ReimbursementType,
  type ReimbursementStatus,
} from '@/hooks'

export const Route = createFileRoute('/_authenticated/finance/reimbursements/' as never)({
  component: ReimbursementListPage,
})



const typeLabels: Record<ReimbursementType, string> = {
  travel: '差旅',
  office: '办公',
  meal: '餐饮',
  transport: '交通',
  other: '其他',
}

const statusLabels: Record<ReimbursementStatus, string> = {
  pending: '待审批',
  approved: '已审批',
  rejected: '已拒绝',
  paid: '已付款',
  cancelled: '已取消',
}

const statusColors: Record<ReimbursementStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-700',
}

function ReimbursementListPage() {
  const [filters, setFilters] = useState<ReimbursementListParams>({ page: 1, pageSize: 20 })
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [payingId, setPayingId] = useState<string | null>(null)
  const [payAccountId, setPayAccountId] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const { data, isLoading, error } = useReimbursements(filters)
  const { data: accounts } = useAccounts()
  const cancelMutation = useCancelReimbursement()
  const approveMutation = useApproveReimbursement()
  const rejectMutation = useRejectReimbursement()
  const payMutation = usePayReimbursement()
  const batchApproveMutation = useBatchApproveReimbursements()
  const batchRejectMutation = useBatchRejectReimbursements()
  const { formatMoney } = useCurrencyFormatter()
  const toast = useToast()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  // Get pending reimbursements for batch selection
  const reimbursements = data?.data ?? []
  const pendingReimbursements = reimbursements.filter((r) => r.status === 'pending')

  const handleCancel = async (id: string) => {
    const confirmed = await confirmWithPreset(
      'cancel',
      '确定要取消此报销申请吗？'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await cancelMutation.mutateAsync(id)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleApprove = (id: string) => {
    void approveMutation.mutateAsync(id)
  }

  const handleReject = () => {
    if (!rejectingId || !rejectReason.trim()) {
      return
    }
    void rejectMutation.mutateAsync({ id: rejectingId, reason: rejectReason }).then(() => {
      setRejectingId(null)
      setRejectReason('')
    })
  }

  const handlePay = () => {
    if (!payingId || !payAccountId) {
      return
    }
    void payMutation.mutateAsync({ id: payingId, accountId: payAccountId }).then(() => {
      setPayingId(null)
      setPayAccountId('')
    })
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  // Batch approval handlers
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    if (selectedIds.length === pendingReimbursements.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(pendingReimbursements.map((r) => r.id))
    }
  }

  const handleBatchApprove = async (ids: string[]) => {
    return new Promise<{ success: string[]; failed: Array<{ id: string; reason: string }> }>(
      (resolve) => {
        batchApproveMutation.mutate(ids, {
          onSuccess: (result) => {
            if (result.success.length > 0) {
              toast.success(`成功批准 ${result.success.length} 项报销`)
            }
            setSelectedIds([])
            resolve(result)
          },
          onError: (error) => {
            toast.error(error.message || '批量审批失败')
            resolve({ success: [], failed: ids.map((id) => ({ id, reason: '操作失败' })) })
          },
        })
      }
    )
  }

  const handleBatchReject = async (ids: string[], reason: string) => {
    return new Promise<{ success: string[]; failed: Array<{ id: string; reason: string }> }>(
      (resolve) => {
        batchRejectMutation.mutate(
          { ids, reason },
          {
            onSuccess: (result) => {
              if (result.success.length > 0) {
                toast.success(`成功拒绝 ${result.success.length} 项报销`)
              }
              setSelectedIds([])
              resolve(result)
            },
            onError: (error) => {
              toast.error(error.message || '批量拒绝失败')
              resolve({ success: [], failed: ids.map((id) => ({ id, reason: '操作失败' })) })
            },
          }
        )
      }
    )
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  const meta = data?.meta
  const activeAccounts = accounts?.filter((a) => a.isActive) ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">报销管理</h1>
          <p className="text-muted-foreground">管理报销申请和审批</p>
        </div>
        <div className="flex gap-2">
          <ExportButton
            type="reimbursements"
            filters={{
              type: filters.type,
              status: filters.status,
              startDate: filters.startDate,
              endDate: filters.endDate,
            }}
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            <Filter className="h-4 w-4" />
            筛选
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            提交报销
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="rounded-lg border bg-muted/30 p-4">
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium">类型</label>
              <Select
                value={filters.type ?? 'all'}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    type: value === 'all' ? undefined : (value as ReimbursementType),
                    page: 1,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="全部类型" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部类型</SelectItem>
                  {Object.entries(typeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">状态</label>
              <Select
                value={filters.status ?? 'all'}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: value === 'all' ? undefined : (value as ReimbursementStatus),
                    page: 1,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="全部状态" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部状态</SelectItem>
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">开始日期</label>
              <input
                type="date"
                value={filters.startDate ?? ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    startDate: e.target.value || undefined,
                    page: 1,
                  }))
                }
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">结束日期</label>
              <input
                type="date"
                value={filters.endDate ?? ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    endDate: e.target.value || undefined,
                    page: 1,
                  }))
                }
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Batch Approval Toolbar */}
      <BatchApprovalToolbar
        selectedIds={selectedIds}
        entityType="reimbursement"
        items={pendingReimbursements.map(r => ({ id: r.id, type: r.type }))}
        onApprove={handleBatchApprove}
        onReject={handleBatchReject}
        onClear={() => setSelectedIds([])}
      />

      {showCreateForm && (
        <ReimbursementForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => setShowCreateForm(false)}
        />
      )}

      {/* 拒绝弹窗 */}
      {rejectingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-semibold">拒绝报销</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="mb-4 w-full rounded-md border px-3 py-2 text-sm"
              placeholder="请输入拒绝原因"
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setRejectingId(null)
                  setRejectReason('')
                }}
                className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
              >
                取消
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="rounded-md bg-destructive px-4 py-2 text-sm text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                确认拒绝
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 付款弹窗 */}
      {payingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-semibold">确认付款</h3>
            {(() => {
              const payingReimbursement = reimbursements.find((r) => r.id === payingId)
              const reimbursementCurrency = payingReimbursement?.currency ?? 'AED'
              const matchingAccounts = activeAccounts.filter(
                (a) => a.currency === reimbursementCurrency
              )
              return (
                <>
                  {payingReimbursement && (
                    <div className="mb-4 rounded-md bg-muted/50 p-3">
                      <p className="text-sm text-muted-foreground">
                        报销金额: {formatMoney(payingReimbursement.amount, reimbursementCurrency)}
                      </p>
                    </div>
                  )}
                  <div className="mb-4">
                    <label className="mb-1 block text-sm font-medium">
                      付款账户 ({reimbursementCurrency})
                    </label>
                    {matchingAccounts.length === 0 ? (
                      <p className="text-sm text-destructive">
                        没有可用的 {reimbursementCurrency} 账户，请先创建对应币种的账户
                      </p>
                    ) : (
                      <Select
                        value={payAccountId || 'placeholder'}
                        onValueChange={(value) => setPayAccountId(value === 'placeholder' ? '' : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="请选择账户" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="placeholder" disabled>请选择账户</SelectItem>
                          {matchingAccounts.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.name} ({account.currency})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </>
              )
            })()}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setPayingId(null)
                  setPayAccountId('')
                }}
                className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
              >
                取消
              </button>
              <button
                onClick={handlePay}
                disabled={!payAccountId}
                className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                确认付款
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : reimbursements.length === 0 ? (
        // Requirements 4.1, 4.2, 4.3, 4.4 - 使用增强空状态组件
        (() => {
          const hasFilters = Boolean(
            filters.status || filters.type || filters.startDate || filters.endDate
          )
          const emptyStateType = determineEmptyStateType({
            hasData: false,
            hasFilters,
            hasError: false,
            isFirstVisit: false,
          })

          if (emptyStateType === 'no-results') {
            return (
              <NoResultsEmptyState
                title="未找到匹配的记录"
                description="尝试调整筛选条件查看更多记录"
                onClearFilters={() => {
                  setFilters({ page: 1, pageSize: 20 })
                }}
              />
            )
          }

          return (
            <EmptyState
              type={emptyStateType}
              title="暂无报销记录"
              description="点击上方按钮提交报销申请"
              icon={Receipt}
              primaryAction={{
                label: '提交报销',
                onClick: () => setShowCreateForm(true),
              }}
            />
          )
        })()
      ) : (
        <>
          {/* 移动端卡片布局 */}
          <div className="space-y-3 md:hidden">
            {/* 移动端全选 */}
            {pendingReimbursements.length > 1 && (
              <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-4 py-3">
                <input
                  type="checkbox"
                  id="select-all-mobile"
                  checked={selectedIds.length === pendingReimbursements.length && pendingReimbursements.length > 0}
                  onChange={toggleSelectAll}
                  className="h-5 w-5 rounded border-gray-300"
                />
                <label htmlFor="select-all-mobile" className="text-sm text-muted-foreground">
                  全选待审批 ({pendingReimbursements.length} 项)
                </label>
              </div>
            )}

            {reimbursements.map((r) => (
              <div key={r.id} className="rounded-lg border bg-card p-4 space-y-3">
                {/* 卡片头部：申请人和状态 */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {r.status === 'pending' && (
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        className="h-5 w-5 rounded border-gray-300"
                      />
                    )}
                    <div>
                      <p className="font-medium">{r.employee?.name ?? '-'}</p>
                      <p className="text-sm text-muted-foreground">{r.employee?.employeeNo}</p>
                    </div>
                  </div>
                  <span
                    className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${statusColors[r.status]}`}
                  >
                    {statusLabels[r.status]}
                  </span>
                </div>

                {/* 卡片内容：金额和类型 */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{formatMoney(r.amount, r.currency)}</p>
                    <p className="text-sm text-muted-foreground">{typeLabels[r.type]}</p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    {r.createdAt.split('T')[0]}
                  </div>
                </div>

                {/* 描述 */}
                {r.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{r.description}</p>
                )}

                {/* 操作按钮 */}
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  {r.status === 'pending' && (
                    <>
                      <UrgeButton approvalType="reimbursement" approvalId={r.id} />
                      <button
                        onClick={() => handleApprove(r.id)}
                        className="flex-1 h-10 rounded-md bg-green-50 text-green-700 text-sm font-medium hover:bg-green-100 active:bg-green-200 flex items-center justify-center gap-1"
                      >
                        <Check className="h-4 w-4" />
                        通过
                      </button>
                      <button
                        onClick={() => setRejectingId(r.id)}
                        className="flex-1 h-10 rounded-md bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 active:bg-red-200 flex items-center justify-center gap-1"
                      >
                        <XIcon className="h-4 w-4" />
                        拒绝
                      </button>
                      <button
                        onClick={() => void handleCancel(r.id)}
                        className="h-10 rounded-md border px-3 text-sm text-muted-foreground hover:bg-muted"
                      >
                        取消
                      </button>
                    </>
                  )}
                  {r.status === 'approved' && (
                    <button
                      onClick={() => setPayingId(r.id)}
                      className="flex-1 h-10 rounded-md bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 active:bg-primary/30 flex items-center justify-center gap-1"
                    >
                      <DollarSign className="h-4 w-4" />
                      付款
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* 桌面端表格布局 */}
          <div className="hidden md:block rounded-lg border">
            {/* Select All for pending items */}
            {pendingReimbursements.length > 1 && (
              <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-2">
                <input
                  type="checkbox"
                  id="select-all-reimbursements"
                  checked={selectedIds.length === pendingReimbursements.length && pendingReimbursements.length > 0}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="select-all-reimbursements" className="text-sm text-muted-foreground">
                  全选待审批 ({pendingReimbursements.length} 项)
                </label>
              </div>
            )}
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="w-10 px-4 py-3"></th>
                  <th className="px-4 py-3 text-left text-sm font-medium">申请人</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">类型</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">金额</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">描述</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">申请时间</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {reimbursements.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3">
                      {r.status === 'pending' && (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(r.id)}
                          onChange={() => toggleSelect(r.id)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{r.employee?.name ?? '-'}</p>
                        <p className="text-sm text-muted-foreground">{r.employee?.employeeNo}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{typeLabels[r.type]}</td>
                    <td className="px-4 py-3 text-right font-medium">
                      {formatMoney(r.amount, r.currency)}
                    </td>
                    <td className="max-w-[200px] truncate px-4 py-3 text-sm text-muted-foreground">
                      {r.description}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[r.status]}`}
                      >
                        {statusLabels[r.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{r.createdAt.split('T')[0]}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {r.status === 'pending' && (
                          <>
                            <UrgeButton approvalType="reimbursement" approvalId={r.id} />
                            <button
                              onClick={() => handleApprove(r.id)}
                              className="rounded p-1 text-green-600 hover:bg-green-50"
                              title="审批通过"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setRejectingId(r.id)}
                              className="rounded p-1 text-red-600 hover:bg-red-50"
                              title="拒绝"
                            >
                              <XIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => void handleCancel(r.id)}
                              className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                            >
                              取消
                            </button>
                          </>
                        )}
                        {r.status === 'approved' && (
                          <button
                            onClick={() => setPayingId(r.id)}
                            className="rounded p-1 text-primary hover:bg-primary/10"
                            title="付款"
                          >
                            <DollarSign className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            共 {meta.total} 条记录，第 {meta.page}/{meta.totalPages} 页
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(meta.page - 1)}
              disabled={meta.page <= 1}
              className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              上一页
            </button>
            <button
              onClick={() => handlePageChange(meta.page + 1)}
              disabled={meta.page >= meta.totalPages}
              className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              下一页
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog />
    </div>
  )
}
